import { useTheme } from '@emotion/react';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import { FormikProvider, useFormik } from 'formik';
import React from 'react';
import { Autocomplete, Box, Chip, CircularProgress, Grid, IconButton, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CustomInputField from 'ui-components/formik/CustomInputField';
import ModalComponent from 'ui-components/ModalComponent';
import { Primary } from 'ui-components/Buttons';
import * as Yup from "yup";
import { useNavigate } from 'react-router-dom';
import { SubscriptionActivatedByAdmin } from 'api/backrr_startups';
import { useToast } from 'context/ToastContext';
import CustomLabel from 'ui-components/CustomLabel';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import CustomValidationText from 'ui-components/CustomValidationText';
import { plans } from 'utils/index';
import { FetchAllPlans } from 'api/utils';
// import { FetchAllOfferCodesData } from 'api/admin_settings';

const useStyles = makeStyles({
    customInput: {},
});

/**
 * A plan as it appears in the selector: the plan and its frequency, with the
 * version as attached metadata rather than part of the name. Plans share a title
 * across versions, so without the version "Plus (Yearly)" appears twice.
 * Superseded versions are muted, matching the offer code screens.
 */
const PlanOptionLabel = ({ option }) => {
    const isCurrent = !option?.version || option.version > 1;

    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: '100%' }}>
            <Box component="span" sx={{ color: isCurrent ? 'inherit' : '#6B7280' }}>
                {`${option?.plan_title || ''} ${option?.period ? option.period.charAt(0).toUpperCase() + option.period.slice(1) : ''}`.trim()}
            </Box>
            {option?.version ? (
                <Box
                    component="span"
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: 1,
                        padding: '3px 6px',
                        borderRadius: '4px',
                        backgroundColor: isCurrent ? '#EEF2FF' : '#F3F4F6',
                        color: isCurrent ? '#4338CA' : '#6B7280',
                        border: `1px solid ${isCurrent ? '#C7D2FE' : '#E5E7EB'}`,
                    }}
                >
                    {`V${option.version}`}
                </Box>
            ) : null}
        </Box>
    );
};

const CreateSubscription = ({ open, handleClose, startup_id, startupDetails, refetch }) => {
    const theme = useTheme();
    const { addToast } = useToast();
    const navigate = useNavigate();

    const { data: allPlans, isLoading: isLoadingPlans } = FetchAllPlans({ startup_id: startupDetails?._id || null });

    // console.log(startupDetails, "startupDetails");

    // Older plans stay activatable — support sometimes needs to put a startup back
    // on an old price — but they share a title with the plans that replaced them,
    // so "Plus (Yearly)" would otherwise appear twice with no way to tell them
    // apart. The version marks which is which, newest first.
    const planOptions = React.useMemo(() => {
        const options = (allPlans?.plans || []).flatMap(plan => {
            // Version and supersession come from the plan itself. `visible` would be
            // wrong here: it only says what is on sale now, and inverts with
            // SHOW_LEGACY_PLANS, which would mark the newest plans as the old ones.
            const version = plan?.version || 1;
            // A plan that was never versioned, like the free tier, gets no suffix.
            // The input can only hold a string, so the selected plan keeps its
            // version in parentheses; the dropdown shows it as a badge instead.
            const versionLabel = plan?.superseded || version > 1 ? ` (V${version})` : '';

            return Object.entries(plan?.razorpay_config || {})
                // Stand-in ids for regions that never launched are not real razorpay
                // plans; activating one would store a plan id nothing can resolve.
                .filter(([, config]) => config?.plan_id && !String(config.plan_id).startsWith('rzp_plan_'))
                .map(([period, config]) => ({
                    label: `${plan?.title} ${period?.charAt(0)?.toUpperCase() + period?.slice(1)}${versionLabel}`,
                    value: config?.plan_id,
                    period: period,
                    plan_title: plan?.title,
                    plan_type: plan?.plan_type,
                    version,
                }));
        });

        return options.sort((a, b) => b.version - a.version);
    }, [allPlans]);

    // React.useEffect(() => {
    //     handleClose()
    // }, [startupDetails?.data?.subscription?.status === "active"])

    // const { data: offerCodes, isLoading: isOfferCodesLoading, refetch: refetchOfferCodes } = FetchAllOfferCodesData({ searchText: "" });
    const { mutateAsync: createSubscriptionByAdmin, isLoading: isCreatingSubscription } = SubscriptionActivatedByAdmin()

    const formik = useFormik({
        initialValues: {
            plan_id: '',
            // quantity: 1,
            // totalCount: 12,
            // offer_id: '',
            // start_at: '',
            // end_at: '',
        },
        validationSchema: Yup.object({
            plan_id: Yup.string().required("Please select a Plan"),
            // quantity: Yup.number().min(1, "Quantity must be at least 1").required("Quantity is required"),
            // totalCount: Yup.number().min(1, "Total count must be at least 1").required("Total count is required"),
            // offer_id: Yup.string(), // optional
            // start_at: Yup.string().required("Start date is required"), // optional, ISO string
            // end_at: Yup.string().required("End date is required"),   // optional, ISO string
        }),
        validateOnBlur: false,
        validateOnChange: false,
        validateOnMount: false,
        enableReinitialize: true,
        onSubmit: async (values, {resetForm}) => {
            const selectedPlan = planOptions.find(plan => plan.value === values.plan_id);
            const payload = {
                startup_id: startupDetails?._id,
                plan_id: values.plan_id,
                plan_type: selectedPlan?.plan_type,
                period: selectedPlan?.period,
                region: allPlans?.region,
                // start_at: values.start_at || undefined,
                // end_at: values.end_at || undefined,
                offer_id: "created-by-admin"
            };

            // console.log(payload, "payload");
            await createSubscriptionByAdmin(payload, {
                onSuccess: () => {
                    addToast("Activated Subscription!", "success")
                    refetch();
                    handleClose();
                },
                onError: () => {
                    addToast("Error Activating Subscription!", "error")
                }
            });
        },
    });

    return (
        <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: "500px", width: "100%" }}>
            <Paper
                sx={{
                    background: theme.palette.customColors.whiteBG,
                    borderRadius: "8px",
                }}
            >
                <IconButton sx={{ position: "absolute", top: 20, right: 20, padding: 0 }}>
                    <CloseIcon width={40} height={40} onClick={handleClose} />
                </IconButton>
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit} onKeyDown={(e) => {
                        if ((e.key === "Enter" && !e.shiftKey)) {
                            e.preventDefault();
                        }
                    }}>
                        <Grid display={"grid"} container padding={"1.5rem"} gap={"1rem"}>
                            <Grid item xs={12}>
                                <Typography
                                    sx={{
                                        fontSize: "1.5rem",
                                        fontWeight: 700,
                                        lineHeight: "28px",
                                        color: theme.palette.customColors.g300,
                                    }}
                                >
                                    Activate Subscription
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <CustomLabel>Select Plan</CustomLabel>
                                <Autocomplete
                                    disablePortal
                                    options={planOptions}
                                    fullWidth
                                    value={
                                        planOptions?.find(item => item?.value === formik?.values?.plan_id) || null
                                    }
                                    autoHighlight
                                    onChange={(_, newValue) => {
                                        formik.setFieldValue("plan_id", newValue ? newValue.value : "");
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.value}>
                                            <PlanOptionLabel option={option} />
                                        </li>
                                    )}

                                    renderInput={(params) => (
                                        <CustomInputField
                                            {...params}
                                            variant="outlined"
                                            name={"plan_id"}
                                            placeholder={"Select Plan"}
                                            sx={{
                                                marginBottom: 0,
                                                "& > .MuiInputBase-root": {
                                                    paddingBlock: "0.2rem !important"
                                                }
                                            }}
                                            className="filter-autocomplete"

                                        />
                                    )}
                                    sx={{
                                    }}
                                />
                            </Grid>

                            {/* <Grid item xs={12}>
                                <CustomLabel>Offer Code (Optional)</CustomLabel>
                                <Autocomplete
                                    options={offerCodes?.data || []}
                                    getOptionLabel={(option) => option.code || ""}
                                    loading={isOfferCodesLoading}
                                    freeSolo
                                    value={
                                        offerCodes?.data?.find(item => item.id === formik.values.offer_id) ||
                                        (formik.values.offer_id ? { code: formik.values.offer_id, id: formik.values.offer_id } : null)
                                    }
                                    onChange={(_, newValue) => {
                                        if (typeof newValue === "string") {
                                            formik.setFieldValue("offer_id", newValue);
                                        } else if (newValue && newValue.id) {
                                            formik.setFieldValue("offer_id", newValue.id);
                                        } else {
                                            formik.setFieldValue("offer_id", "");
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <CustomInputField
                                            {...params}
                                            name="offer_id"
                                            // label="Offer Code or Custom ID"
                                            placeholder="Select or enter offer code"
                                            value={formik.values.offer_id}
                                            onChange={formik.handleChange}
                                            // sx={{ marginBottom: 1 }}
                                            tabIndex={4}
                                        />
                                    )}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            borderRadius: "4px"
                                        }
                                    }}
                                />
                            </Grid> */}

                            {/* <Grid item xs={12} paddingBottom={0}>
                                <CustomLabel>START DATE</CustomLabel>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label=""
                                        sx={{ width: "100%", background: "#fff", marginBottom: 0 }}
                                        value={formik.values?.start_at ? moment(formik.values.start_at) : null}
                                        disableFuture={false}
                                        onChange={(date) => {
                                            formik.setFieldValue('start_at', date != null ? date.toISOString() : null);
                                        }}
                                        renderInput={(params) => (
                                            <CustomInputField
                                                sx={{ padding: "10px 14px" }}
                                                fullWidth
                                                {...params}
                                            />
                                        )}
                                        format="DD/MM/YYYY"
                                        autoComplete="off"
                                    />
                                </LocalizationProvider>
                                {formik.errors?.start_at && <CustomValidationText value={formik.errors.start_at} />}
                            </Grid>
                            <Grid item xs={12} paddingBottom={0}>
                                <CustomLabel>END DATE</CustomLabel>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label=""
                                        sx={{ width: "100%", background: "#fff", marginBottom: 0 }}
                                        value={formik.values?.end_at ? moment(formik.values.end_at) : null}
                                        disableFuture={false}
                                        onChange={(date) => {
                                            formik.setFieldValue('end_at', date != null ? date.toISOString() : null);
                                        }}
                                        renderInput={(params) => (
                                            <CustomInputField
                                                sx={{ padding: "10px 14px" }}
                                                fullWidth
                                                {...params}
                                            />
                                        )}
                                        format="DD/MM/YYYY"
                                        autoComplete="off"
                                    />
                                </LocalizationProvider>
                                {formik.errors?.end_at && <CustomValidationText value={formik.errors.end_at} />}
                            </Grid> */}

                            <Grid item xs={12}>
                                <Primary
                                    type="submit"
                                    fullWidth
                                    sx={{ padding: "0.75rem 1.25rem", fontSize: "1rem", height: '52px' }}
                                    disabled={isCreatingSubscription}
                                >
                                    {isCreatingSubscription ? <CircularProgress sx={{ color: "white", marginInline: 'auto' }} size={'1.2rem'} /> : "Activate"}
                                </Primary>
                            </Grid>
                        </Grid>
                    </form>
                </FormikProvider>
            </Paper>
        </ModalComponent>
    );
};

export default CreateSubscription;
